import React, { useRef, useCallback, useState, useEffect } from 'react';
import { ILogt } from '../../models/logT';
import { formatZuluGridDateTime } from '../../utils/zuluTime';

interface BetaLogTGridProps {
  data: ILogt[];
  totalCount: number;
  isLoading: boolean;
  onLoadMore: () => void;
  hasMore: boolean;
  onSort: (column: string) => void;
  sortColumn: string;
  sortDirection: string;
  selectedId: number | null;
  onRowSelect: (row: ILogt) => void;
  onCancelSearch?: () => void;
}

const columns = [
  { key: 'logtTS',               label: 'Date/Time',  width: '130px' },
  { key: 'Direction',            label: 'Dir',        width: '75px'  },
  { key: 'MessageType',          label: 'Type',       width: '220px'  },
  { key: 'SMI',                  label: 'SMI',        width: '65px'  },
  { key: 'Medium',               label: 'Medium',     width: '75px'  },
  { key: 'AircraftId',           label: 'Aircraft',   width: '80px'  },
  { key: 'FlightId',             label: 'Flight',     width: '80px'  },
  { key: 'Source',               label: 'Source',     width: '160px' },
  { key: 'DepartureAirportIATA', label: 'Orig',       width: '58px'  },
  { key: 'ArrivalAirportIATA',   label: 'Dest',       width: '58px'  },
];

const formatCell = (row: ILogt, key: string): string => {
  const r = row as any;
  if (key === 'logtTS') {
    const ts = r.logtTS || r.LogtTS;
    return formatZuluGridDateTime(ts);
  }
  if (key === 'Direction') {
    const dir = r.Direction ?? r.direction;
    if (dir === 0 || dir === '0') return '↑ Uplink';
    if (dir === 1 || dir === '1') return '↓ Downlink';
    return String(dir ?? '');
  }
  const lo = key.charAt(0).toLowerCase() + key.slice(1);
  return String(r[key] ?? r[lo] ?? r[key.toLowerCase()] ?? '');
};

const BetaLogTGrid: React.FC<BetaLogTGridProps> = ({
  data, totalCount, isLoading, onLoadMore, hasMore,
  onSort, sortColumn, sortDirection, selectedId, onRowSelect, onCancelSearch,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastScrollRef = useRef(0);
  const [searchDuration, setSearchDuration] = useState(0);
  const searchTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Track how long the search has been running
  useEffect(() => {
    if (isLoading && data.length === 0) {
      setSearchDuration(0);
      searchTimerRef.current = setInterval(() => {
        setSearchDuration(prev => prev + 1);
      }, 1000);
    } else {
      if (searchTimerRef.current) {
        clearInterval(searchTimerRef.current);
        searchTimerRef.current = null;
      }
      setSearchDuration(0);
    }
    return () => {
      if (searchTimerRef.current) {
        clearInterval(searchTimerRef.current);
      }
    };
  }, [isLoading, data.length]);

  const handleScroll = useCallback(() => {
    if (isLoading || !hasMore) return;
    const el = scrollRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    if (scrollHeight - scrollTop - clientHeight < 400) {
      const now = Date.now();
      if (now - lastScrollRef.current < 500) return;
      lastScrollRef.current = now;
      onLoadMore();
    }
  }, [hasMore, isLoading, onLoadMore]);

  const sortIcon = (key: string) => {
    if (sortColumn?.toLowerCase() !== key.toLowerCase()) return null;
    return <span className="beta-sort-icon">{sortDirection === 'DESC' ? '↓' : '↑'}</span>;
  };

  return (
    <div className="beta-grid-container">
      {/* Scrollable table */}
      <div className="beta-grid-scroll-wrapper" ref={scrollRef} onScroll={handleScroll}>
        <table className="beta-grid-table">
          <colgroup>
            {columns.map(col => <col key={col.key} style={{ width: col.width }} />)}
          </colgroup>
          <thead className="beta-grid-thead">
            <tr>
              {columns.map(col => (
                <th
                  key={col.key}
                  className={`beta-grid-th ${sortColumn?.toLowerCase() === col.key.toLowerCase() ? 'beta-grid-th--active' : ''}`}
                  onClick={() => onSort(col.key)}
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter') onSort(col.key); }}
                >
                  {col.label}{sortIcon(col.key)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="beta-grid-tbody">
            {data.map((row) => (
              <tr
                key={row.logtId}
                className={`beta-grid-tr ${selectedId === row.logtId ? 'beta-grid-tr--selected' : ''}`}
                tabIndex={0}
                onClick={() => onRowSelect(row)}
                onKeyDown={(e) => { if (e.key === 'Enter') onRowSelect(row); }}
              >
                {columns.map(col => {
                  const cellValue = formatCell(row, col.key);
                  return (
                    <td key={col.key} className="beta-grid-td" title={cellValue}>
                      {cellValue}
                    </td>
                  );
                })}
              </tr>
            ))}
            
            {/* Manual Load More button inside table */}
            {!isLoading && hasMore && data.length > 0 && (
              <tr>
                <td colSpan={columns.length} style={{ textAlign: 'center', padding: '16px' }}>
                  <button className="beta-grid-load-more-btn" onClick={onLoadMore} type="button" style={{ width: '200px' }}>
                    Load More
                  </button>
                </td>
              </tr>
            )}

            {/* Loading more inside grid: Skeleton Rows */}
            {isLoading && data.length > 0 && Array.from({ length: 3 }).map((_, i) => (
              <tr key={`skeleton-${i}`} className="beta-grid-tr beta-grid-tr--skeleton">
                {columns.map((col, j) => (
                  <td key={`skeleton-col-${col.key}`} className="beta-grid-td">
                    <div className="beta-skeleton-pulse" style={{ width: `${80 - (j % 3) * 10}%` }}></div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>

        {/* Empty state */}
        {!isLoading && data.length === 0 && (
          <div className="beta-grid-empty">
            Use the filters above to search for ACARS messages.
          </div>
        )}

        {/* Inline loading with search status */}
        {isLoading && data.length === 0 && (
          <div className="beta-grid-search-overlay">
            <div className="beta-grid-spinner beta-grid-spinner--large" />
            <p className="beta-grid-search-message">
              {searchDuration < 30
                ? 'Searching for messages...'
                : 'This search is taking longer than expected. Please wait...'}
            </p>
            {searchDuration >= 10 && onCancelSearch && (
              <button
                type="button"
                className="beta-grid-cancel-btn"
                onClick={onCancelSearch}
              >
                Cancel Search
              </button>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default BetaLogTGrid;
